// Feature: docs/features/athena-integration-master.md
import React, { useState, useContext } from 'react';
import {
  Box,
  Button,
  Heading,
  Text,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Alert,
  AlertIcon,
  Code,
  VStack,
  Input,
  useToast,
  HStack,
} from '@chakra-ui/react';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';

const AthenaDemo = () => {
  // State for existing functionality
  const [patients, setPatients] = useState([]);
  const [familyName, setFamilyName] = useState('Sandboxtest');
  const [givenName, setGivenName] = useState('');
  const [limit, setLimit] = useState(10);
  const [patientId, setPatientId] = useState('a-195900.E-60179');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rawData, setRawData] = useState(null);
  const { accessToken } = useContext(AppContext);
  const toast = useToast();

  const fetchDemoRecords = async () => {
    setIsFetchingDemos(true);
    try {
      const response = await callApi('/api/athena/my-demos', accessToken);
      if (!response.ok) {
        throw new Error('Failed to fetch demo records.');
      }
      const data = await response.json();
      setDemoRecords(data.demos || []);
    } catch (err) {
      toast({
        title: 'Error fetching demo records',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsFetchingDemos(false);
    }
  };

  React.useEffect(() => {
    if (accessToken) {
      fetchDemoRecords();
    }
  }, [accessToken]);


  // State for new patient creation workflow
  const [newFirstName, setNewFirstName] = useState('John');
  const [newLastName, setNewLastName] = useState('Appleseed');
  const [newDob, setNewDob] = useState('04/01/1985');
  const [isCreating, setIsCreating] = useState(false);
  const [creationResult, setCreationResult] = useState(null);
  const [useStubMode, setUseStubMode] = useState(true);  // Default to stub for reliability

  // State for demo records list
  const [demoRecords, setDemoRecords] = useState([]);
  const [isFetchingDemos, setIsFetchingDemos] = useState(false);


  const handleCreatePatientAndEncounter = async () => {
    setIsCreating(true);
    setError(null);
    setCreationResult(null);
    setRawData(null);

    if (!accessToken) {
        setError("No access token available. Please log in.");
        setIsCreating(false);
        return;
    }

    try {
      const payload = {
        firstname: newFirstName,
        lastname: newLastName,
        dob: newDob,
      };

      const endpoint = useStubMode 
        ? '/api/athena/create-stubbed-patient-data' 
        : '/api/athena/create-patient-data';

      const response = await callApi(endpoint, accessToken, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `API Error: ${response.statusText}`);
      }

      const data = await response.json();
      
      setCreationResult(data);
      setRawData(JSON.stringify(data, null, 2));
      toast({
        title: 'Success!',
        description: data.message,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      // Refresh the list of demo records
      fetchDemoRecords();

    } catch (err) {
      setError(err.message || 'An unknown error occurred.');
      setRawData(JSON.stringify(err, null, 2));
    } finally {
      setIsCreating(false);
    }
  };

  const fetchPatients = async () => {
    setIsLoading(true);
    setError(null);
    setPatients([]);
    setRawData(null);

    if (!accessToken) {
        setError("No access token available. Please log in.");
        return;
    }

    try {
      const query = new URLSearchParams({
        family: familyName,
        given: givenName,
        limit: limit,
      }).toString();

      const response = await callApi(`/api/athena/patients?${query}`, accessToken);
      
      if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `API Error: ${response.statusText}`);
      }

      const data = await response.json();
      
      setPatients(data);
      setRawData(JSON.stringify(data, null, 2));

    } catch (err) {
      setError(err.message || 'An unknown error occurred.');
      setRawData(JSON.stringify(err, null, 2));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPatientById = async () => {
    setIsLoading(true);
    setError(null);
    setPatients([]);
    setRawData(null);

    if (!accessToken) {
        setError("No access token available. Please log in.");
        return;
    }

    try {
      const response = await callApi(`/api/athena/patient/${patientId}`, accessToken);
      
      if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `API Error: ${response.statusText}`);
      }

      const data = await response.json();
      
      // The API returns a single object, but our table expects an array
      setPatients([data]); 
      setRawData(JSON.stringify(data, null, 2));

    } catch (err) {
      setError(err.message || 'An unknown error occurred.');
      setRawData(JSON.stringify(err, null, 2));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box p={8}>
      <VStack spacing={6} align="stretch">
        <Heading as="h1" size="xl">Athenahealth API Demo</Heading>
        <Text>
          This page demonstrates interactions with the Athenahealth API. You can create a new patient with a full clinical encounter or use the read-only tools to search existing patients.
        </Text>

        <Box p={4} borderWidth="1px" borderRadius="lg" bg="blue.50">
          <Heading as="h2" size="lg" mb={3}>1. Create Patient & Encounter</Heading>
          <Text mb={4}>This will create a new patient, book a visit, check them in, and inject a sample SOAP note for Hypertension.</Text>
          <VStack spacing={3} align="stretch">
            <HStack>
              <Input
                placeholder="First Name"
                value={newFirstName}
                onChange={(e) => setNewFirstName(e.target.value)}
                bg="white"
              />
              <Input
                placeholder="Last Name"
                value={newLastName}
                onChange={(e) => setNewLastName(e.target.value)}
                bg="white"
              />
            </HStack>
            <Input
              placeholder="Date of Birth (MM/DD/YYYY)"
              value={newDob}
              onChange={(e) => setNewDob(e.target.value)}
              bg="white"
            />
            <HStack justify="space-between" align="center">
              <Box>
                <Text fontSize="sm" color="gray.600" mb={1}>
                  {useStubMode ? "☑️ Stub Mode (Reliable Demo)" : "🔴 Live Athena (Sandbox - May Flake)"}
                </Text>
                <Button
                  size="sm"
                  variant={useStubMode ? "solid" : "outline"}
                  onClick={() => setUseStubMode(!useStubMode)}
                  colorScheme="purple"
                >
                  Toggle {useStubMode ? "Live" : "Stub"}
                </Button>
              </Box>
              <Button
                colorScheme="blue"
                onClick={handleCreatePatientAndEncounter}
                isLoading={isCreating}
                loadingText={useStubMode ? "Generating Stub..." : "Creating Workflow..."}
              >
                {useStubMode ? "Run Stubbed Demo" : "Run Live Workflow"}
              </Button>
            </HStack>
          </VStack>
        </Box>

        {creationResult && (
          <Box p={4} borderWidth="1px" borderRadius="lg" bg="green.50">
            <Heading as="h3" size="md" mb={2}>Creation Workflow Result</Heading>
            <Text><strong>Patient ID:</strong> {creationResult.patientId}</Text>
            <Text><strong>Encounter ID:</strong> {creationResult.encounterId}</Text>
            <Text><strong>Department ID:</strong> {creationResult.departmentId}</Text>
            <Text mt={2}>✅ {creationResult.message}</Text>
          </Box>
        )}
        
        <Heading as="h2" size="lg" mt={6}>My Demo Records</Heading>
        <Box p={4} borderWidth="1px" borderRadius="lg">
          {isFetchingDemos ? (
            <Box textAlign="center">
              <Spinner />
              <Text>Loading demo records...</Text>
            </Box>
          ) : demoRecords.length > 0 ? (
            <TableContainer>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Patient ID</Th>
                    <Th>Encounter ID</Th>
                    <Th>Created At</Th>
                    <Th>Note Preview</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {demoRecords.map((rec) => (
                    <Tr key={rec.id}>
                      <Td>{rec.patient_id}</Td>
                      <Td>{rec.encounter_id}</Td>
                      <Td>{new Date(rec.created_at).toLocaleString()}</Td>
                      <Td>{rec.preview_text}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          ) : (
            <Text>No demo records found. Create a new patient to see it here.</Text>
          )}
        </Box>

        <Heading as="h2" size="lg" mt={6}>Read-Only Tools</Heading>

        <Box p={4} borderWidth="1px" borderRadius="lg">
          <Heading as="h2" size="lg" mb={3}>Search Patients by Name</Heading>
          <VStack spacing={3} align="stretch">
            <Input
              placeholder="Family name (e.g., Sandboxtest)"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
            />
            <Input
              placeholder="Given name (optional)"
              value={givenName}
              onChange={(e) => setGivenName(e.target.value)}
            />
            <Input
              placeholder="Number of results"
              type="number"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
            />
            <Button
              colorScheme="brand"
              onClick={fetchPatients}
              isLoading={isLoading}
              loadingText="Searching..."
            >
              Search by Name
            </Button>
          </VStack>
        </Box>

        <Box p={4} borderWidth="1px" borderRadius="lg">
          <Heading as="h2" size="lg" mb={3}>Get Patient by ID</Heading>
          <VStack spacing={3} align="stretch">
            <Input
              placeholder="Patient FHIR ID"
              value={patientId}
              onChange={(e) => setPatientId(e.target.value)}
            />
            <Button
              colorScheme="teal"
              onClick={fetchPatientById}
              isLoading={isLoading}
              loadingText="Fetching..."
            >
              Fetch by ID
            </Button>
          </VStack>
        </Box>

        {isLoading && (
          <Box textAlign="center">
            <Spinner size="xl" />
            <Text mt={2}>Contacting Athenahealth API...</Text>
          </Box>
        )}

        {error && (
          <Alert status="error">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">Failed to fetch data:</Text>
              <Code colorScheme="red" mt={2} p={2} display="block" whiteSpace="pre-wrap">
                {error}
              </Code>
            </Box>
          </Alert>
        )}

        {patients.length > 0 && (
          <Box>
            <Heading as="h2" size="lg" mb={4}>Patient List</Heading>
            <TableContainer borderWidth="1px" borderRadius="lg">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Full Name</Th>
                    <Th>MRN</Th>
                    <Th>Gender</Th>
                    <Th>Birth Date</Th>
                    <Th>Athena ID</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {patients.map((patient) => (
                    <Tr key={patient.id}>
                      <Td>{patient.full_name}</Td>
                      <Td>{patient.mrn}</Td>
                      <Td>{patient.gender}</Td>
                      <Td>{patient.birthDate}</Td>
                      <Td>{patient.id}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {rawData && (
            <Box>
                <Heading as="h3" size="md" mb={2}>Raw API Response</Heading>
                <Code p={4} display="block" whiteSpace="pre-wrap" borderRadius="md" bg="gray.50">
                    {rawData}
                </Code>
            </Box>
        )}

      </VStack>
    </Box>
  );
};

export default AthenaDemo;